import type { JmapResponse } from "./types";

export class JmapCallError extends Error {
  constructor(
    message: string,
    readonly method?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "JmapCallError";
  }
}

type SetErrorEntry = { type?: string; description?: string; properties?: string[] };

function formatSetErrors(method: string, errors: Record<string, SetErrorEntry>): string {
  const parts = Object.entries(errors).map(([id, e]) => {
    const props = e.properties?.length ? ` [${e.properties.join(", ")}]` : "";
    return `${id}: ${e.type ?? "error"}${e.description ? ` — ${e.description}` : ""}${props}`;
  });
  return `${method} failed: ${parts.join("; ")}`;
}

export function findMethodResponse(
  response: JmapResponse,
  method: string,
): [string, Record<string, unknown>, string] | undefined {
  return response.methodResponses.find(([name]) => name === method);
}

export function block<T = Record<string, unknown>>(response: JmapResponse, method: string): T {
  const item = findMethodResponse(response, method);
  if (!item) {
    const emailSet = findMethodResponse(response, "Email/set");
    if (emailSet?.[1].notCreated) {
      const nc = emailSet[1].notCreated as Record<string, SetErrorEntry>;
      throw new JmapCallError(formatSetErrors("Email/set", nc), "Email/set", nc);
    }
    const errors = response.methodResponses.filter(([name]) => name === "error");
    if (errors.length) {
      const [, body] = errors[0];
      const type = String(body.type ?? "error");
      const desc = body.description ? `: ${body.description}` : "";
      throw new JmapCallError(`${type}${desc}`, method, response.methodResponses);
    }
    throw new JmapCallError(`Missing method response: ${method}`, method, response.methodResponses);
  }

  const body = item[1];
  const notCreated = body.notCreated as Record<string, SetErrorEntry> | undefined;
  if (notCreated && Object.keys(notCreated).length > 0) {
    throw new JmapCallError(formatSetErrors(method, notCreated), method, notCreated);
  }
  const notUpdated = body.notUpdated as Record<string, SetErrorEntry> | undefined;
  if (notUpdated && Object.keys(notUpdated).length > 0) {
    throw new JmapCallError(formatSetErrors(method, notUpdated), method, notUpdated);
  }

  return body as T;
}

export function jmapDebugEnabled(): boolean {
  return (
    process.env.NEXT_PUBLIC_ORIGINMAIL_JMAP_DEBUG === "true" ||
    process.env.NODE_ENV === "development"
  );
}

export function logJmap(label: string, data: unknown): void {
  if (!jmapDebugEnabled()) return;
  console.debug(`[originmail:jmap] ${label}`, data);
}
